// Resolves whatever an upload rejection carries into something the UI can always render.
//
// The v2 upload API reports failures three different ways, and the modal must render whichever
// one arrives:
//   - v1-style row errors:  response.data.invalidTntRecords  [{ rowNumber, invalidColumnMessages }]
//   - v2 parse rollups:     response.data.errorInfo.parseErrorCounts  [{ count, errorDetail, errorLocation }]
//   - plain rejections:     response.data.message  (e.g. "Existing tnt data set version ... exist")
//
// The `message` fallback is last and always non-empty, so no path can render a blank modal.

export interface InvalidTntRecord {
  rowNumber: number;
  invalidColumnMessages?: string[];
}

export interface UploadParseErrorRollup {
  count?: number;
  errorDetail?: string;
  errorLocation?: string;
}

export type UploadErrorDetail =
  | { kind: 'rows'; rows: InvalidTntRecord[] }
  | { kind: 'rollups'; rollups: UploadParseErrorRollup[] }
  | { kind: 'message'; message: string };

export const UPLOAD_ERROR_FALLBACK = 'The upload was rejected, but the server did not say why.';

interface UploadErrorPayload {
  invalidTntRecords?: unknown;
  errorInfo?: {
    parseErrorCounts?: unknown;
    errorMessage?: unknown;
    errorCode?: unknown;
    invalidRowCount?: unknown;
  } | null;
  message?: unknown;
  error?: unknown;
}

const asPayload = (value: unknown): UploadErrorPayload =>
  value && typeof value === 'object' ? (value as UploadErrorPayload) : {};

const nonEmptyArray = (value: unknown): value is unknown[] => Array.isArray(value) && value.length > 0;

const firstNonEmptyString = (...candidates: unknown[]): string | undefined =>
  candidates.find((c): c is string => typeof c === 'string' && c.trim().length > 0);

// Shared by the synchronous rejection path and the async job-failure path: both carry the same
// invalidTntRecords / errorInfo shapes, just at different roots.
const fromPayload = (raw: unknown, ...extraMessages: unknown[]): UploadErrorDetail => {
  const payload = asPayload(raw);

  if (nonEmptyArray(payload.invalidTntRecords)) {
    return { kind: 'rows', rows: payload.invalidTntRecords as InvalidTntRecord[] };
  }

  if (nonEmptyArray(payload.errorInfo?.parseErrorCounts)) {
    return { kind: 'rollups', rollups: payload.errorInfo?.parseErrorCounts as UploadParseErrorRollup[] };
  }

  const message = firstNonEmptyString(
    payload.message,
    payload.errorInfo?.errorMessage,
    payload.errorInfo?.errorCode,
    payload.error,
    ...extraMessages,
  );

  return { kind: 'message', message: message ?? UPLOAD_ERROR_FALLBACK };
};

export const resolveUploadError = (error: unknown): UploadErrorDetail => {
  const { response, message } = asPayload(error) as { response?: { data?: unknown }; message?: unknown };
  return fromPayload(response?.data, message);
};

export const JOB_FAILED_FALLBACK = 'The upload job failed, but the server did not say why.';

// A job that reaches uploadJobStatus 'FAILED' carries its detail on errorInfo rather than on an
// axios response. `invalidRowCount` is the last resort so a row-count failure still says something.
export const resolveJobError = (job: unknown): UploadErrorDetail => {
  const detail = fromPayload(job);

  if (detail.kind === 'message' && detail.message === UPLOAD_ERROR_FALLBACK) {
    const rowCount = asPayload(job).errorInfo?.invalidRowCount;
    if (typeof rowCount === 'number' && rowCount > 0) {
      return { kind: 'message', message: `${rowCount} record(s) in the file were invalid.` };
    }
    return { kind: 'message', message: JOB_FAILED_FALLBACK };
  }

  return detail;
};

// The status-cell modal in the job tables shows the raw server message rather than the richer
// resolveJobError detail. Extracted here so TntTable and TntCarrierDefaultTable share ONE
// definition (they had already drifted apart on the error-download path), and so the
// null-errorInfo fallback is testable without rendering a table.
//
// Master dereferenced `row.errorInfo.errorMessage.toString()` at both call sites, which threw a
// TypeError for any job that failed before the parse stage and therefore never populated
// errorInfo — the exact rows a user is most likely to click.
export const jobFailureMessage = (row: { errorInfo?: { errorMessage?: string | null } | null }): string =>
  row?.errorInfo?.errorMessage ?? JOB_FAILED_FALLBACK;
